// Slice de UI da feature dashboard. Só estado de visualização (período selecionado).
// Dados ficam no cache do RTK Query (api.reducer).
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { DashboardPeriod } from './api';

/** Loja selecionada no dashboard. `'ALL'` = todas; `null` = ainda não resolvido
 * (cai no default "primeira loja criada" assim que a lista carrega). */
export type DashboardStoreFilter = string | 'ALL' | null;

/** RF-02: intervalo custom (date-only YYYY-MM-DD). Espelha ManagementCustomRange. */
export interface DashboardCustomRange {
  from: string;
  to: string;
}

export interface DashboardUiState {
  selectedPeriod: DashboardPeriod;
  /** Não-nulo ⇒ intervalo custom ativo (sobrepõe o preset nas queries). */
  customRange: DashboardCustomRange | null;
  selectedStoreId: DashboardStoreFilter;
}

const initialState: DashboardUiState = {
  selectedPeriod: 'today',
  customRange: null,
  selectedStoreId: null,
};

const slice = createSlice({
  name: 'dashboardUi',
  initialState,
  reducers: {
    // Escolher um preset sai do modo custom (RF-02).
    dashboardPeriodChanged(state, action: PayloadAction<DashboardPeriod>) {
      state.selectedPeriod = action.payload;
      state.customRange = null;
    },
    dashboardCustomRangeChanged(state, action: PayloadAction<DashboardCustomRange>) {
      state.customRange = action.payload;
    },
    // Default "primeira loja": só seta enquanto ninguém escolheu nada ainda, pra
    // não sobrescrever a escolha do usuário num re-render posterior.
    dashboardStoreInitialized(state, action: PayloadAction<string>) {
      if (state.selectedStoreId === null) {
        state.selectedStoreId = action.payload;
      }
    },
    dashboardStoreChanged(state, action: PayloadAction<string | 'ALL'>) {
      state.selectedStoreId = action.payload;
    },
  },
});

export const {
  dashboardPeriodChanged,
  dashboardCustomRangeChanged,
  dashboardStoreInitialized,
  dashboardStoreChanged,
} = slice.actions;
export const dashboardUiReducer = slice.reducer;

export const selectDashboardPeriod = (s: { dashboardUi: DashboardUiState }): DashboardPeriod =>
  s.dashboardUi.selectedPeriod;

export const selectDashboardCustomRange = (s: {
  dashboardUi: DashboardUiState;
}): DashboardCustomRange | null => s.dashboardUi.customRange;

export const selectDashboardStore = (s: { dashboardUi: DashboardUiState }): DashboardStoreFilter =>
  s.dashboardUi.selectedStoreId;

/** Filtro de período resolvido para as queries (RF-02): quando há `customRange`,
 * manda `from`/`to` (envelope volta `period:'custom'`); senão só o preset. */
export const selectDashboardPeriodRange = (s: {
  dashboardUi: DashboardUiState;
}): { period: DashboardPeriod; from?: string; to?: string } => {
  const { selectedPeriod, customRange } = s.dashboardUi;
  return customRange
    ? { period: selectedPeriod, from: customRange.from, to: customRange.to }
    : { period: selectedPeriod };
};
